/**
 * Skin analysis API client.
 * Centralizes detect-skin and condition-descriptions requests.
 */

import { getApiBaseUrl, SALON_API_PREFIX } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';
import { fetchJsonWithRetry } from './fetchWithRetry';
import { compressDetectImages, compressDataUrl } from './imageCompress';
import { TIMEOUT_MS, RETRY } from './salonResilience';

const DETECT_SKIN_PATH = `${SALON_API_PREFIX}/detect-skin`;
const DESCRIPTIONS_PATH = `${SALON_API_PREFIX}/generate-condition-descriptions`;
const STORED_DESCRIPTIONS_PATH = `${SALON_API_PREFIX}/get-stored-condition-descriptions`;
const STORED_VISION_INSIGHT_PATH = `${SALON_API_PREFIX}/stored-vision-insight`;
const SKIN_AGE_PATH = `${SALON_API_PREFIX}/skin-age`;
const GENERATE_ROUTINE_PATH = `${SALON_API_PREFIX}/generate-routine`;
const GENERATE_MASK_PATH = `${SALON_API_PREFIX}/generate-skin-condition-mask`;
const MACRO_CAPTURE_PATH = `${SALON_API_PREFIX}/macro/capture`;
const MACRO_ANALYZE_PATH = `${SALON_API_PREFIX}/macro/analyze`;
const MACRO_RESULT_PATH = `${SALON_API_PREFIX}/macro/result`;

/** Match typical detect-skin duration so UI steps do not finish before the API. */
const DETECT_PROGRESS_STEP_MS = 5200;

const SINGLE_DETECT_PROGRESS_STEPS = [
  { phase: 'upload', message: 'Uploading your photo securely…' },
  { phase: 'scan', message: 'Scanning facial features…' },
  { phase: 'detect', message: 'Detecting skin conditions…' },
  { phase: 'score', message: 'Calculating your skin score…' },
  { phase: 'finalize', message: 'Preparing your results…' },
];

const BATCH_DETECT_PROGRESS_STEPS = [
  { phase: 'upload', message: 'Uploading your captures…' },
  { phase: 'scan', message: 'Processing front and side views…' },
  { phase: 'detect', message: 'Detecting conditions across all views…' },
  { phase: 'score', message: 'Aggregating your skin score…' },
  { phase: 'finalize', message: 'Preparing your personalized report…' },
];

function startDetectProgressTicker(onProgress, { batchSize, isBatch }) {
  if (typeof onProgress !== 'function') {
    return { stop() {}, pause() {}, resume() {} };
  }

  const steps = isBatch ? BATCH_DETECT_PROGRESS_STEPS : SINGLE_DETECT_PROGRESS_STEPS;
  let stepIndex = 0;
  let paused = false;
  let resumeTimerId = null;

  const emitStep = () => {
    if (paused) return;
    const step = steps[stepIndex];
    onProgress({
      phase: step.phase,
      message: step.message,
      batchSize,
      viewsCompleted: 0,
      stepIndex,
      stepCount: steps.length,
    });
  };

  emitStep();

  const intervalId = setInterval(() => {
    if (paused || stepIndex >= steps.length - 1) return;
    stepIndex += 1;
    emitStep();
  }, DETECT_PROGRESS_STEP_MS);

  return {
    stop: () => {
      if (resumeTimerId) clearTimeout(resumeTimerId);
      clearInterval(intervalId);
    },
    pause: () => {
      paused = true;
    },
    resume: () => {
      paused = false;
    },
    resumeAfter: (delayMs) => {
      if (resumeTimerId) clearTimeout(resumeTimerId);
      resumeTimerId = setTimeout(() => {
        resumeTimerId = null;
        paused = false;
      }, delayMs);
    },
  };
}

/**
 * @returns {string} Normalized API base URL (no trailing slash).
 */
function getBaseUrl() {
  const raw = getApiBaseUrl();
  return (raw || '').replace(/\/$/, '');
}

/**
 * Build full URL for an API path.
 * @param {string} path - e.g. '/api/salon/detect-skin'
 * @returns {string}
 */
export function buildSkinAnalysisApiUrl(path) {
  const base = getBaseUrl();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

/**
 * Run batched skin detection (front + optional side captures in one request).
 */
export async function detectSkinBatch({
  frontImage,
  sideCaptures,
  sessionId,
  age,
  allowedConditions,
  appSessionToken,
  useOllamaForDescriptions,
  masksPolicy,
  retryCount = 0,
  onProgress,
  signal,
  onRetry,
  requestId,
}) {
  const front = frontImage || null;
  if (!front) {
    throw new Error('Front image is required');
  }

  const compressed = await compressDetectImages(front, sideCaptures);
  const side_captures = {};
  if (compressed.sideCaptures?.left) side_captures.left = compressed.sideCaptures.left;
  if (compressed.sideCaptures?.right) side_captures.right = compressed.sideCaptures.right;
  const sideCount = Object.keys(side_captures).length;
  const batchSize = 1 + sideCount;
  const progressTicker = startDetectProgressTicker(onProgress, {
    batchSize,
    isBatch: sideCount > 0,
  });

  const handleRetry = (info) => {
    progressTicker.pause();
    if (typeof onRetry === 'function') {
      onRetry(info);
    }
    progressTicker.resumeAfter(Number(info?.delayMs) || 0);
  };

  const url = buildSkinAnalysisApiUrl(DETECT_SKIN_PATH);
  const body = {
    image: compressed.frontImage,
    front_image: compressed.frontImage,
    conf_threshold: 0.06,
    session_id: sessionId ?? null,
    age: age ?? null,
    batch: sideCount > 0,
    retry_count: retryCount,
    ...(sideCount > 0 && { side_captures }),
    ...(side_captures.left && { left_image: side_captures.left }),
    ...(side_captures.right && { right_image: side_captures.right }),
    ...(allowedConditions && allowedConditions.length > 0 && { allowed_conditions: allowedConditions }),
    ...(appSessionToken && { app_session_token: appSessionToken }),
    ...(typeof useOllamaForDescriptions === 'boolean' && { use_ollama_for_descriptions: useOllamaForDescriptions }),
    ...(masksPolicy && { masks_policy: masksPolicy }),
  };

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (requestId && String(requestId).trim()) {
      headers['X-Request-ID'] = String(requestId).trim();
    }
    const data = await fetchJsonWithRetry(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        timeoutMs: sideCount > 0 ? TIMEOUT_MS.BATCH_DETECT : TIMEOUT_MS.SINGLE_DETECT,
        signal,
      },
      { label: 'salon-detect-skin-batch', onRetry: handleRetry, conflictMaxAttempts: RETRY.CONFLICT_MAX_ATTEMPTS }
    );

    if (typeof onProgress === 'function') {
      onProgress({
        phase: 'finalize',
        message: 'Preparing your results…',
        batchSize,
        viewsCompleted: batchSize,
        stepIndex: 4,
        stepCount: 5,
      });
    }

    return data;
  } finally {
    progressTicker.stop();
  }
}

/** @deprecated Use detectSkinBatch */
export async function detectSkin(params) {
  return detectSkinBatch({
    frontImage: params.image,
    sessionId: params.sessionId,
    age: params.age,
    allowedConditions: params.allowedConditions,
    appSessionToken: params.appSessionToken,
    useOllamaForDescriptions: params.useOllamaForDescriptions,
    masksPolicy: params.masksPolicy,
  });
}

/** @deprecated Use detectSkinBatch */
export async function detectSkinWithSideCaptures(params) {
  return detectSkinBatch(params);
}

/**
 * Fetch AI-generated condition descriptions (Ollama).
 */
export async function fetchConditionDescriptions(payload) {
  if (!payload) return {};
  const url = buildSkinAnalysisApiUrl(DESCRIPTIONS_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 60000,
  });
  const data = await response.json().catch(() => ({}));
  if (!data.success || !data.descriptions || typeof data.descriptions !== 'object') {
    return {};
  }
  return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
}

/**
 * Fetch stored AI condition descriptions (fast; no Ollama call).
 */
export async function fetchStoredConditionDescriptions({
  sessionId,
  appSessionToken,
  generateIfMissing = false,
  requestedConditions,
  previousSkinData,
}) {
  if (!sessionId) return {};
  if (previousSkinData) {
    const url = buildSkinAnalysisApiUrl(STORED_DESCRIPTIONS_PATH);
    const body = {
      session_id: sessionId,
      ...(appSessionToken && { app_session_token: appSessionToken }),
      generate_if_missing: generateIfMissing,
      ...(Array.isArray(requestedConditions) && requestedConditions.length > 0
        ? { requested_conditions: requestedConditions }
        : {}),
      previous_skin_data: previousSkinData,
    };
    const response = await fetchWithTimeout(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success || !data.descriptions || typeof data.descriptions !== 'object') {
      return {};
    }
    return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
  }

  const url = new URL(buildSkinAnalysisApiUrl(STORED_DESCRIPTIONS_PATH));
  url.searchParams.set('session_id', sessionId);
  if (appSessionToken) url.searchParams.set('app_session_token', appSessionToken);
  url.searchParams.set('generate_if_missing', generateIfMissing ? 'true' : 'false');
  if (Array.isArray(requestedConditions) && requestedConditions.length > 0) {
    url.searchParams.set('requested_conditions', requestedConditions.join(','));
  }
  const response = await fetchWithTimeout(url.toString(), { timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success || !data.descriptions || typeof data.descriptions !== 'object') {
    return {};
  }
  return Object.keys(data.descriptions).length > 0 ? data.descriptions : {};
}

/**
 * Lazily generate a single skin concern mask for a session.
 */
export async function generateSkinConditionMask({ sessionId, condition, appSessionToken }) {
  const url = buildSkinAnalysisApiUrl(GENERATE_MASK_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      condition,
      ...(appSessionToken && { app_session_token: appSessionToken }),
    }),
    timeoutMs: TIMEOUT_MS.SINGLE_DETECT,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || response.statusText || 'Mask request failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}

/**
 * Load persisted Qwen3-VL skin age + photo insight from SalonAIContent.
 */
export async function fetchStoredVisionInsight({ sessionId, appSessionToken }) {
  if (!sessionId) return null;
  const url = new URL(buildSkinAnalysisApiUrl(STORED_VISION_INSIGHT_PATH));
  url.searchParams.set('session_id', sessionId);
  if (appSessionToken) url.searchParams.set('app_session_token', appSessionToken);
  const response = await fetchWithTimeout(url.toString(), { timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success || !data?.stored) return null;
  return data;
}

/**
 * Estimate skin age + type via Qwen3-VL (persists when session_id provided).
 */
export async function requestSkinAge({
  capturedImage,
  sessionId,
  appSessionToken,
  aqiData,
  signal,
}) {
  if (!capturedImage && !sessionId) {
    throw new Error('capturedImage or sessionId is required');
  }
  const visionImage = capturedImage
    ? await compressDataUrl(capturedImage, { maxSide: 448, quality: 0.72 })
    : null;
  const url = buildSkinAnalysisApiUrl(SKIN_AGE_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(visionImage ? { captured_image: visionImage } : {}),
      ...(sessionId ? { session_id: sessionId } : {}),
      ...(appSessionToken ? { app_session_token: appSessionToken } : {}),
      ...(aqiData && typeof aqiData === 'object' ? { aqi_data: aqiData } : {}),
    }),
    timeoutMs: TIMEOUT_MS.SKIN_AGE,
    signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success) {
    const error = new Error(data.error || response.statusText || 'Skin age request failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}

/**
 * Prefetch summary + tips + routine during analysis loader (skip vision — use /skin-age).
 */
export async function requestGenerateRoutine({
  detections,
  skinScore,
  skinConditions,
  userData,
  sessionId,
  appSessionToken,
  aqiData,
  previousSkinData,
  useOllamaForSummary = true,
  useOllamaForRoutine = true,
  forceRegenerate = true,
  skipVisionInsight = true,
  signal,
}) {
  const url = buildSkinAnalysisApiUrl(GENERATE_ROUTINE_PATH);
  const leanSkinConditions = skinConditions && typeof skinConditions === 'object'
    ? Object.fromEntries(
      Object.entries(skinConditions).map(([k, v]) => {
        if (!v || typeof v !== 'object') return [k, v];
        const { mask, ...rest } = v;
        return [k, rest];
      })
    )
    : skinConditions;

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      detections: detections || [],
      skin_score: skinScore || null,
      previous_skin_data: previousSkinData || null,
      skin_conditions: leanSkinConditions,
      user_data: {
        ...(userData || {}),
        session_id: sessionId,
      },
      aqi_data: aqiData || null,
      session_id: sessionId,
      ...(appSessionToken ? { app_session_token: appSessionToken } : {}),
      ai_generated_summary: useOllamaForSummary,
      use_ollama_for_summary: useOllamaForSummary,
      use_ollama_for_routine: useOllamaForRoutine,
      skip_product_recommendations: true,
      skip_vision_insight: skipVisionInsight,
      force_regenerate: forceRegenerate,
      force_regenerate_vision: false,
    }),
    timeoutMs: TIMEOUT_MS.GENERATE_ROUTINE,
    signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success) {
    const error = new Error(data.error || response.statusText || 'Generate routine failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}

/**
 * Upload one USB microscope skin region (forehead | cheek).
 */
export async function uploadSkinMacroCapture({
  sessionId,
  region,
  image,
  appSessionToken,
}) {
  if (!sessionId) throw new Error('sessionId is required');
  if (!region) throw new Error('region is required');
  if (!image) throw new Error('image is required');
  const url = buildSkinAnalysisApiUrl(MACRO_CAPTURE_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      region,
      image,
      ...(appSessionToken && { app_session_token: appSessionToken }),
    }),
    timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    const error = new Error(data.error || response.statusText || 'Macro capture failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}

/**
 * Run Qwen + OpenCV overlay analysis on stored macro captures.
 */
export async function analyzeSkinMacro({
  sessionId,
  macroCaptures,
  appSessionToken,
  signal,
}) {
  if (!sessionId) throw new Error('sessionId is required');
  const url = buildSkinAnalysisApiUrl(MACRO_ANALYZE_PATH);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      session_id: sessionId,
      ...(macroCaptures && typeof macroCaptures === 'object' ? { macro_captures: macroCaptures } : {}),
      ...(appSessionToken && { app_session_token: appSessionToken }),
    }),
    timeoutMs: TIMEOUT_MS.MACRO_ANALYZE || TIMEOUT_MS.SINGLE_DETECT,
    signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.success) {
    const error = new Error(data.error || response.statusText || 'Macro analysis failed');
    error.status = response.status;
    error.apiResponse = data;
    throw error;
  }
  return data;
}

/**
 * Load persisted macro analysis for a session.
 */
export async function fetchSkinMacroResult({ sessionId, appSessionToken }) {
  if (!sessionId) return null;
  const url = new URL(buildSkinAnalysisApiUrl(MACRO_RESULT_PATH));
  url.searchParams.set('session_id', sessionId);
  if (appSessionToken) url.searchParams.set('app_session_token', appSessionToken);
  const response = await fetchWithTimeout(url.toString(), { timeoutMs: TIMEOUT_MS.SAVE_ANALYSIS });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success) return null;
  return data.macro || null;
}
